import { useState } from 'react';
import {
  WidgetContainerRequest,
  FilterListContainer,
  ArrowDownIcon,
  ArrowUpIcon,
  DismissIcon,
  FilterIcon,
} from './styles/RequestsWidget.styled';
import { VacationsRequest } from './models/RequestWidget.interface';
import { VacationType } from 'src/tabs/hooks/useVacationTypes';
import { truncateString } from 'src/utils/truncateString';
import { RequestIcon } from './RequestIcon';
import { formatDate } from 'src/utils/dateFns/formatDate';
import { Tooltip } from '@fluentui/react-components';
import { StatusEnum } from 'src/utils/mappers/checkStatusCode';
import moment from 'moment';
import { filterByDateOptions } from 'src/tabs/hooks/useRequestFilters';
import { AXSTable } from 'src/components/common/AxsTable/AXSTable';
import { AXSTableSelect } from 'src/components/common/AxsTable/AXSTableSelect';
import {
  TableOption,
  FilterAXSCheckbox,
} from 'src/components/common/AxsTable/styles/AXSTableSelect.styled';
import {
  generateReactKey,
  KeyRoots,
} from 'src/utils/generateUniqueReactKey/generateUniqueReactKey';

export enum TableRowEnum {
  STATUS = 'statuscode',
  STARTDATE = 'new_startdate',
  ENDDATE = 'new_enddate',
  VACATIONTYPE = '_new_vacationtype_value',
}

interface RequestsWidgetProps {
  requests: VacationsRequest[];
  getUserRequests: (
    statusCode?: string[],
    vacationTypeId?: string[],
    vacationTypes?: VacationType[]
  ) => Promise<void>;
  showRequestPopup: () => void;
  requestIdHandler: (requestId: string) => void;
  loadingRequest: boolean;
  vacationStatusHandler: (value: string) => void;
  vacationTypeHandler: (value: string) => void;
  filterDateHandler: (value: string, type: string) => void;
  clearFilterDateHandler: () => void;
  clearVacationStatusHandler: () => void;
  clearVacationTypeHandler: () => void;
  vacationStatus: string[];
  vacationType: string[];
  filterDate: Date | 'Full Year';
  filterDateType: string;
  filterDateTypeHandler: (value: string) => void;
  filterDateYearAndMonthHandler: (value: string, sort) => void;
  filterDirection: string;
  filterDirectionHandler: (value: string) => void;
  filterDirectionResetHandler: () => void;
  sortBy: string;
  mappedVacationTypes: {
    key: string;
    text: string;
  }[];
}

export const RequestsWidget = ({
  mappedVacationTypes,
  requests,
  showRequestPopup,
  requestIdHandler,
  loadingRequest,
  clearFilterDateHandler,
  clearVacationStatusHandler,
  clearVacationTypeHandler,
  filterDate,
  filterDateHandler,
  vacationStatus,
  vacationStatusHandler,
  vacationType,
  vacationTypeHandler,
  filterDateType,
  filterDateYearAndMonthHandler,
  filterDirectionHandler,
  filterDirection,
  sortBy,
}: RequestsWidgetProps) => {
  const [dateByDirection, setDateByDirection] = useState('start');
  const [isFilterActive, setIsFilterActive] = useState({
    statusFilter: false,
    startDateFilter: false,
    endDateFilter: false,
    vacationTypeFilter: false,
  });

  const closeAllFilters = () => {
    setIsFilterActive({
      statusFilter: false,
      startDateFilter: false,
      endDateFilter: false,
      vacationTypeFilter: false,
    });
  };

  const startDateSorting =
    filterDirection === 'asc' ? 'Start date ↑' : 'Start date ↓';

  const endDateSorting =
    filterDirection === 'asc' ? 'End date ↑' : 'End date ↓';

  const statusFilterOptions = [
    StatusEnum.WAITING_FOR_APPROVAL,
    StatusEnum.DOCUMENTS,
    StatusEnum.PROCESSED,
    StatusEnum.COMPLETED,
    StatusEnum.CANCELLED,
    StatusEnum.REJECTED,
  ];

  const monthsInYear = {
    January: '0',
    February: '1',
    March: '2',
    April: '3',
    May: '4',
    June: '5',
    July: '6',
    August: '7',
    September: '8',
    October: '9',
    November: '10',
    December: '11',
  };

  const dateFiltersActive =
    filterDateType !== 'In' || filterDate !== 'Full Year';

  const formattedDateString =
    moment(filterDate).format('MMMM') === 'Invalid date'
      ? 'Full Year'
      : moment(filterDate).format('MMMM');

  const headerData = [
    [
      <AXSTableSelect
        header="Status"
        key="status"
        closeAllFilters={closeAllFilters}
        filterOptionsHeader={isFilterActive.statusFilter && 'Filter by'}
      >
        {!isFilterActive.statusFilter ? (
          <>
            <TableOption
              onClick={() =>
                setIsFilterActive((prev) => {
                  return { ...prev, statusFilter: true };
                })
              }
            >
              <FilterListContainer>
                <FilterIcon /> Filter By
              </FilterListContainer>
            </TableOption>
            {vacationStatus.length > 0 && (
              <TableOption onClick={clearVacationStatusHandler}>
                <FilterListContainer key="clearFilters">
                  <DismissIcon /> Clear all filters
                </FilterListContainer>
              </TableOption>
            )}
          </>
        ) : (
          <>
            <TableOption>
              <FilterAXSCheckbox
                labelPosition="after"
                label={'All'}
                size="medium"
                onChange={() => vacationStatusHandler('All')}
                checked={vacationStatus.length === 0}
              />
            </TableOption>
            {statusFilterOptions.map((option, index) => {
              return (
                <TableOption
                  key={generateReactKey(
                    KeyRoots.TIME_OFF_REQUEST_WIDGET_STATUS_FILTER_OPTIONS,
                    index
                  )}
                >
                  <FilterAXSCheckbox
                    labelPosition="after"
                    label={option}
                    size="medium"
                    onChange={() => vacationStatusHandler(option)}
                    checked={vacationStatus.includes(option)}
                  />
                </TableOption>
              );
            })}
          </>
        )}
      </AXSTableSelect>,
    ],
    [
      <AXSTableSelect
        header={dateByDirection === 'start' ? startDateSorting : 'Start date'}
        key="startDate"
        closeAllFilters={closeAllFilters}
        position="relative"
        optionContainerPadding={isFilterActive.startDateFilter ? '0.5rem' : '0'}
        filterOptionsHeader={isFilterActive.startDateFilter && 'Filter by'}
      >
        {!isFilterActive.startDateFilter ? (
          <>
            <TableOption
              onClick={() => {
                setDateByDirection('start');
                filterDirectionHandler('asc');
              }}
            >
              <FilterListContainer key="sortAscending">
                Sort ascending <ArrowUpIcon />
              </FilterListContainer>
            </TableOption>
            <TableOption
              onClick={() => {
                setDateByDirection('start');
                filterDirectionHandler('desc');
              }}
            >
              <FilterListContainer key="sortDescending">
                Sort descending <ArrowDownIcon />
              </FilterListContainer>
            </TableOption>
            <TableOption
              onClick={() =>
                setIsFilterActive((prev) => {
                  return { ...prev, startDateFilter: true };
                })
              }
            >
              <FilterListContainer key="filterBy">
                <FilterIcon /> Filter By
              </FilterListContainer>
            </TableOption>
            {dateFiltersActive && (
              <TableOption onClick={clearFilterDateHandler}>
                <FilterListContainer key="clearFilters">
                  <DismissIcon /> Clear all filters
                </FilterListContainer>
              </TableOption>
            )}
          </>
        ) : (
          <>
            <AXSTableSelect
              header={sortBy === 'startDate' ? filterDateType : 'In'}
              key="startDateSecondInput"
              inputStylePicker={true}
              right="-115px"
            >
              {filterByDateOptions.map((option, index) => (
                <TableOption
                  key={generateReactKey(
                    KeyRoots.TIME_OFF_REQUEST_WIDGET_FILTER_BY_DATE__OPTIONS,
                    index
                  )}
                  onClick={() => filterDateHandler(option, 'startDate')}
                >
                  <FilterListContainer key={option}>
                    {option}
                  </FilterListContainer>
                </TableOption>
              ))}
            </AXSTableSelect>
            <AXSTableSelect
              header={
                sortBy === 'startDate' ? formattedDateString : 'Full Year'
              }
              key="startDate"
              inputStylePicker={true}
              scrollable
              right="-115px"
            >
              <TableOption
                onClick={() =>
                  filterDateYearAndMonthHandler('Full Year', 'startDate')
                }
              >
                <FilterListContainer>Full Year</FilterListContainer>
              </TableOption>
              {Object.entries(monthsInYear).map(([key, value], index) => (
                <TableOption
                  key={generateReactKey(
                    KeyRoots.TIME_OFF_REQUEST_WIDGET_MONTH_IN_YEAR_OPTION,
                    index
                  )}
                  onClick={() =>
                    filterDateYearAndMonthHandler(value, 'startDate')
                  }
                >
                  <FilterListContainer key={key}>{key}</FilterListContainer>
                </TableOption>
              ))}
            </AXSTableSelect>
          </>
        )}
      </AXSTableSelect>,
    ],
    [
      <AXSTableSelect
        header={dateByDirection === 'end' ? endDateSorting : 'End date'}
        key="endDate"
        closeAllFilters={closeAllFilters}
        position="relative"
        optionContainerPadding={isFilterActive.endDateFilter ? '0.5rem' : '0'}
        filterOptionsHeader={isFilterActive.endDateFilter && 'Filter by'}
      >
        {!isFilterActive.endDateFilter ? (
          <>
            <TableOption
              onClick={() => {
                setDateByDirection('end');
                filterDirectionHandler('asc');
              }}
            >
              <FilterListContainer key="sortAscending">
                Sort ascending <ArrowUpIcon />
              </FilterListContainer>
            </TableOption>
            <TableOption
              onClick={() => {
                setDateByDirection('end');
                filterDirectionHandler('desc');
              }}
            >
              <FilterListContainer key="sortDescending">
                Sort descending <ArrowDownIcon />
              </FilterListContainer>
            </TableOption>
            <TableOption
              onClick={() =>
                setIsFilterActive((prev) => {
                  return { ...prev, endDateFilter: true };
                })
              }
            >
              <FilterListContainer key="filterBy">
                <FilterIcon /> Filter By
              </FilterListContainer>
            </TableOption>
            {dateFiltersActive && (
              <TableOption onClick={clearFilterDateHandler}>
                <FilterListContainer key="clearFilters">
                  <DismissIcon /> Clear all filters
                </FilterListContainer>
              </TableOption>
            )}
          </>
        ) : (
          <>
            <AXSTableSelect
              header={sortBy === 'endDate' ? filterDateType : 'In'}
              key="endDate"
              inputStylePicker={true}
              right="115px"
            >
              {filterByDateOptions.map((option) => (
                <TableOption
                  key={option}
                  onClick={() => filterDateHandler(option, 'endDate')}
                >
                  <FilterListContainer key={option}>
                    {option}
                  </FilterListContainer>
                </TableOption>
              ))}
            </AXSTableSelect>
            <AXSTableSelect
              header={sortBy === 'endDate' ? formattedDateString : 'Full Year'}
              inputStylePicker={true}
              scrollable
              right="115px"
            >
              <TableOption
                onClick={() =>
                  filterDateYearAndMonthHandler('Full Year', 'endDate')
                }
              >
                <FilterListContainer>Full Year</FilterListContainer>
              </TableOption>
              {Object.entries(monthsInYear).map(([key, value]) => (
                <TableOption
                  key={key}
                  onClick={() =>
                    filterDateYearAndMonthHandler(value, 'endDate')
                  }
                >
                  <FilterListContainer key={key}>{key}</FilterListContainer>
                </TableOption>
              ))}
            </AXSTableSelect>
          </>
        )}
      </AXSTableSelect>,
    ],
    [
      <AXSTableSelect
        scrollable
        closeAllFilters={closeAllFilters}
        header="Vacation type"
        key="vacationType"
        right={'1rem'}
        width={'190px'}
        filterOptionsHeader={isFilterActive.vacationTypeFilter && 'Filter by'}
      >
        {!isFilterActive.vacationTypeFilter ? (
          <>
            <TableOption
              onClick={() =>
                setIsFilterActive((prev) => {
                  return { ...prev, vacationTypeFilter: true };
                })
              }
            >
              <FilterListContainer key="filterBy">
                <FilterIcon /> Filter By
              </FilterListContainer>
            </TableOption>

            {vacationType.length > 0 && (
              <TableOption onClick={clearVacationTypeHandler}>
                <FilterListContainer key="clearFilters">
                  <DismissIcon /> Clear all filters
                </FilterListContainer>
              </TableOption>
            )}
          </>
        ) : (
          <>
            <TableOption>
              <FilterAXSCheckbox
                labelPosition="after"
                label={'All'}
                size="medium"
                onChange={() => vacationTypeHandler('All')}
                checked={vacationType.length === 0}
              />
            </TableOption>
            {mappedVacationTypes.map((option) => (
              <TableOption key={option.key}>
                <FilterAXSCheckbox
                  labelPosition="after"
                  label={option.text}
                  size="medium"
                  onChange={() => vacationTypeHandler(option.key)}
                  checked={vacationType.includes(option.key)}
                />
              </TableOption>
            ))}
          </>
        )}
      </AXSTableSelect>,
    ],
  ];

  const tableRow = Object.values(TableRowEnum);
  const mappedRequests = requests.map((request) => ({
    ...request,
    statuscode: (
      <Tooltip
        content={request.statuscode_Formatted}
        relationship="description"
      >
        <div>
          <RequestIcon requestStatus={request.statuscode_Formatted} />
        </div>
      </Tooltip>
    ),
    new_startdate: `${formatDate(new Date(request.new_startdate)).day}/${
      formatDate(new Date(request.new_startdate)).month
    }/${formatDate(new Date(request.new_startdate)).year}`,
    new_enddate: `${formatDate(new Date(request.new_enddate)).day}/${
      formatDate(new Date(request.new_enddate)).month
    }/${formatDate(new Date(request.new_enddate)).year}`,
    _new_vacationtype_value: truncateString(
      request._new_vacationtype_value_Formatted.replace('Time-off', 'Time Off'),
      13
    ),
  }));

  return (
    <WidgetContainerRequest>
      <AXSTable
        requests={mappedRequests}
        headerData={headerData}
        tableRow={tableRow}
        requestIdHandler={requestIdHandler}
        showRequestPopup={showRequestPopup}
        loadingRequest={loadingRequest}
      />
    </WidgetContainerRequest>
  );
};
